<template>
  <div class="container audio-container">
    <div class="audio-container-show">
      <div class="audio-container-reset" @click="handleReset">
        <i class="iconfont icon-zhongzhi"></i>
        <span>重置</span>
      </div>
      <div class="audio-container-show-name" v-if="audioFile && audioFile.name">
        {{ audioFile.name ? audioFile.name : "" }}
      </div>
      <div
        id="waveform"
        ref="waveform"
        @mousemove="onWaveformHover"
        @mouseleave="onWaveformLeave"
      >
        <div
          class="control-bar-left"
          v-show="audioLoaded"
          ref="leftControlBar"
          @mousedown="startDrag('left', $event)"
        ></div>
        <div
          class="control-bar-right"
          v-show="audioLoaded"
          ref="rightControlBar"
          @mousedown="startDrag('right', $event)"
        ></div>
        <div class="hover-indicator" ref="hoverIndicator">
          <span>{{ this.hoverTime }}</span>
        </div>
      </div>
      <div class="wave-func">
        <i
          :class="`iconfont ${
            isPlaying ? 'icon-zanting' : 'icon-bofang'
          } player-icon`"
          @click="playPause"
        ></i>
        <div class="split-time">
          <div>开始时间：{{ playbackRange.startTime }}</div>
          <div>结束时间：{{ playbackRange.endTime }}</div>
        </div>
        <div class="download pointer">
          <div @click="downloadFile">下载</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WaveSurfer from "wavesurfer.js";

export default {
  name: "AudioWaveComponent",
  props: {
    audioFile: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      wavesurfer: null,
      audioLoaded: false,
      isPlaying: false,
      currentTime: "0:00",
      duration: "0:00",
      volume: 0.8,
      loading: false,
      currentPlayIndicator: null,
      error: "",
      markers: [],
      markerCount: 0,
      hoverTime: "00:00",
      controlBars: {
        left: {
          isDragging: false,
          startClientX: 0,
          startLeft: 0,
          current: 0,
        },
        right: {
          isDragging: false,
          startClientX: 0,
          startLeft: 0,
          current: 0,
        },
      },
      minGap: 10,
      playbackRange: {
        startTime: "00:00",
        endTime: "00:00",
      },
      // 新增：记录是否刚刚拖动过控制条
      justDragged: false,
      // 新增：记录是否应该从范围开始播放
      shouldPlayFromRangeStart: false,
    };
  },

  methods: {
    initWavesurfer() {
      this.wavesurfer = WaveSurfer.create({
        container: this.$refs.waveform,
        waveColor: "#00AF62",
        progressColor: "#1B5572",
        cursorColor: "transparent",
        barWidth: 2,
        barRadius: 3,
        cursorWidth: 0,
        height: 128,
        responsive: true,
        normalize: true,
        backend: "WebAudio",
        fillParent: true,
        scrollParent: false,
      });

      // 绑定事件监听器
      this.wavesurfer.on("ready", () => {
        this.audioLoaded = true;
        this.loading = false;
        const durationInSeconds = this.wavesurfer.getDuration();
        this.duration = this.formatTime(durationInSeconds);

        // 初始化播放范围
        this.playbackRange = {
          startTime: "00:00",
          endTime: this.duration,
        };

        // 初始化左控制条位置（播放起始点）
        this.moveLeftControllerBar();

        // 初始化右控制条位置（音频末尾）
        this.moveRightControllerBar();

        if (this.$loading && this.$loading().close) {
          this.$loading().close();
        }
      });

      this.wavesurfer.on("audioprocess", () => {
        this.currentTime = this.formatTime(this.wavesurfer.getCurrentTime());

        // 更新播放指示器位置
        if (this.currentPlayIndicator && this.isPlaying) {
          const currentTime = this.wavesurfer.getCurrentTime();
          const duration = this.wavesurfer.getDuration();
          const waveformWidth = this.$refs.waveform.offsetWidth;

          const positionPercent = (currentTime / duration) * 100;
          const leftPosition = (positionPercent / 100) * waveformWidth - 10;

          this.currentPlayIndicator.style.left = `${leftPosition}px`;
          this.currentPlayIndicator.style.display = "block";
        }

        // 监控是否到达右控制条
        if (this.isPlaying) {
          const currentTime = this.wavesurfer.getCurrentTime();
          const waveformWidth = this.$refs.waveform.offsetWidth;

          const endPercent =
            ((this.controlBars.right.current + 10) / waveformWidth) * 100;
          const duration = this.wavesurfer.getDuration();
          const endTime = (endPercent / 100) * duration;

          if (currentTime >= endTime) {
            this.wavesurfer.pause();
            this.isPlaying = false;

            // 到达范围末尾时，标记为下次应该从范围开始播放
            this.shouldPlayFromRangeStart = true;

            // 隐藏播放指示器
            if (this.currentPlayIndicator) {
              this.currentPlayIndicator.style.display = "none";
            }
          }
        }
      });

      this.wavesurfer.on("play", () => {
        this.isPlaying = true;
        // 开始播放后，重置刚刚拖动的标志
        this.justDragged = false;
      });

      this.wavesurfer.on("pause", () => {
        this.isPlaying = false;
      });

      this.wavesurfer.on("seek", () => {
        // 当用户拖动进度条时也要更新控制条位置
        this.moveLeftControllerBar();
      });

      this.wavesurfer.on("finish", () => {
        this.isPlaying = false;
      });

      this.wavesurfer.on("error", (error) => {
        this.error = `音频加载失败: ${error}`;
        this.loading = false;
        this.audioLoaded = false;
        if (this.$loading && this.$loading().close) {
          this.$loading().close();
        }
      });

      this.wavesurfer.setVolume(this.volume);
    },

    playPause() {
      if (this.wavesurfer) {
        if (this.isPlaying) {
          // 如果正在播放，则暂停
          this.wavesurfer.pause();
        } else {
          // 如果暂停状态，判断是否需要从范围开始播放
          if (this.shouldPlayFromRangeStart || this.justDragged) {
            // 从范围开始播放
            const startPercent =
              ((this.controlBars.left.current + 10) /
                this.$refs.waveform.offsetWidth) *
              100;
            this.wavesurfer.seekTo(startPercent / 100);
            this.shouldPlayFromRangeStart = false;
            this.justDragged = false;
          }
          // 否则，从当前位置继续播放
          this.wavesurfer.play();
        }
      }
    },

    moveLeftControllerBar() {
      // 如果左控制条正在拖动，不自动更新位置
      if (this.controlBars.left.isDragging) return;

      if (!this.wavesurfer || !this.audioLoaded || !this.$refs.leftControlBar) {
        return;
      }

      const duration = this.wavesurfer.getDuration();
      const currentTime = this.wavesurfer.getCurrentTime();

      // 计算百分比
      const positionPercent = (currentTime / duration) * 100;

      // 使用百分比设置left
      const waveformWidth = this.$refs.waveform.offsetWidth;
      let leftPosition = (positionPercent / 100) * waveformWidth - 10;

      // 确保左控制条不超过右控制条（减去最小间距）
      const rightBarLeft = this.controlBars.right.current || waveformWidth - 10;
      leftPosition = Math.min(leftPosition, rightBarLeft - this.minGap);

      this.$refs.leftControlBar.style.left = `${leftPosition}px`;
      this.controlBars.left.current = leftPosition;
    },

    moveRightControllerBar() {
      if (
        !this.wavesurfer ||
        !this.audioLoaded ||
        !this.$refs.rightControlBar
      ) {
        return;
      }

      // 右控制条默认在音频末尾（100%位置）
      const positionPercent = 100;

      // 计算left值
      const waveformWidth = this.$refs.waveform.offsetWidth;
      let leftPosition = (positionPercent / 100) * waveformWidth - 10;

      // 确保右控制条不落后于左控制条（加上最小间距）
      const leftBarLeft = this.controlBars.left.current || -10;
      leftPosition = Math.max(leftPosition, leftBarLeft + this.minGap);

      this.$refs.rightControlBar.style.left = `${leftPosition}px`;
      this.controlBars.right.current = leftPosition;
    },

    // 通用的开始拖动方法
    startDrag(barType, event) {
      event.preventDefault();
      event.stopPropagation();

      // 1. 设置拖动状态
      this.controlBars[barType].isDragging = true;

      // 2. 获取控制条元素
      const barRef =
        barType === "left"
          ? this.$refs.leftControlBar
          : this.$refs.rightControlBar;

      // 3. 解析当前left值（从样式字符串中提取）
      const currentLeftStr = barRef.style.left;
      let currentLeft = 0;

      if (currentLeftStr) {
        // 如果是像 "100px" 这样的字符串，直接转换为数字
        if (currentLeftStr.includes("px")) {
          currentLeft = parseFloat(currentLeftStr);
        }
        // 如果是calc表达式，如 "calc(10% - 10px)"
        else if (currentLeftStr.includes("calc")) {
          const match = currentLeftStr.match(/calc\(([\d.]+)%/);
          if (match && match[1]) {
            const waveformWidth = this.$refs.waveform.offsetWidth;
            const percent = parseFloat(match[1]);
            currentLeft = (percent / 100) * waveformWidth - 10;
          }
        }
      } else {
        // 如果没有样式，使用默认位置
        const waveformWidth = this.$refs.waveform.offsetWidth;
        if (barType === "left") {
          currentLeft = -10;
        } else {
          currentLeft = waveformWidth - 10;
        }
      }

      // 4. 记录初始值
      this.controlBars[barType].startClientX = event.clientX;
      this.controlBars[barType].startLeft = currentLeft;
      this.controlBars[barType].current = currentLeft;

      // 5. 暂停播放（可选）
      if (this.wavesurfer && this.isPlaying) {
        this.wavesurfer.pause();
      }

      // 6. 添加全局事件监听
      document.addEventListener("mousemove", this.handleDragMove);
      document.addEventListener("mouseup", () => this.stopDrag(barType));
    },

    // 通用的拖动移动方法
    handleDragMove(event) {
      // 检查哪个控制条在拖动
      let barType = null;
      if (this.controlBars.left.isDragging) {
        barType = "left";
      } else if (this.controlBars.right.isDragging) {
        barType = "right";
      }

      // 如果没有拖动，直接返回
      if (!barType) return;

      // 1. 计算鼠标移动距离
      const offsetX = event.clientX - this.controlBars[barType].startClientX;

      // 2. 计算新的left值
      let newLeft = this.controlBars[barType].startLeft + offsetX;

      // 3. 获取波形图宽度
      const waveformWidth = this.$refs.waveform.offsetWidth;

      // 4. 根据拖动的是左控制条还是右控制条，应用不同的限制规则
      if (barType === "left") {
        // 左控制条不能超过右控制条（减去最小间距）
        const rightBarLeft =
          this.controlBars.right.current || waveformWidth - 10;
        const maxLeft = Math.min(
          waveformWidth - 10,
          rightBarLeft - this.minGap
        );
        newLeft = Math.max(-10, Math.min(maxLeft, newLeft));
      } else if (barType === "right") {
        // 右控制条不能落后于左控制条（加上最小间距）
        const leftBarLeft = this.controlBars.left.current || -10;
        const minLeft = Math.max(-10, leftBarLeft + this.minGap);
        newLeft = Math.max(minLeft, Math.min(waveformWidth - 10, newLeft));
      }

      // 5. 更新控制条位置
      let barRef =
        barType === "left"
          ? this.$refs.leftControlBar
          : this.$refs.rightControlBar;

      barRef.style.left = `${newLeft}px`;
      this.controlBars[barType].current = newLeft;

      // 6. 计算对应的百分比和时间并跳转
      this.updateTimeFromPosition(barType, newLeft, waveformWidth);
    },

    // 通用的停止拖动方法
    stopDrag(barType) {
      if (!this.controlBars[barType].isDragging) return;

      // 1. 停止拖动
      this.controlBars[barType].isDragging = false;

      // 2. 移除事件监听
      document.removeEventListener("mousemove", this.handleDragMove);

      // 3. 重置该控制条的状态（但保留currentLeft）
      this.controlBars[barType] = {
        ...this.controlBars[barType],
        isDragging: false,
        startClientX: 0,
        startLeft: 0,
      };

      // 4. 标记刚刚拖动过控制条，下次播放应从范围开始
      this.justDragged = true;

      // 5. 隐藏播放指示器
      const playIndicator = document.querySelector(".play-indicator");
      if (playIndicator) {
        playIndicator.style.display = "none";
      }
    },

    // 更新时间和跳转
    updateTimeFromPosition(barType, leftValue, waveformWidth) {
      // 1. 计算百分比（控制条中心位置）
      const positionPercent = ((leftValue + 10) / waveformWidth) * 100;

      // 2. 计算对应的时间
      if (this.wavesurfer && this.audioLoaded) {
        const duration = this.wavesurfer.getDuration();
        const targetTime = (positionPercent / 100) * duration;

        // 3. 更新悬停时间显示，添加控制条标识
        const timeStr = this.formatTime(targetTime);
        this.hoverTime =
          barType === "left" ? `左: ${timeStr}` : `右: ${timeStr}`;

        // 4. 更新对应控制条的时间
        if (barType === "left") {
          this.playbackRange.startTime = timeStr;
          // 拖动左控制条时跳转到对应位置
          this.wavesurfer.seekTo(positionPercent / 100);
        } else if (barType === "right") {
          this.playbackRange.endTime = timeStr;
        }
      }
    },

    handleReset() {
      this.$emit("reset");
      if (this.wavesurfer) {
        this.wavesurfer.stop();
        this.wavesurfer.seekTo(0);
        this.isPlaying = false;
        this.shouldPlayFromRangeStart = false;
        this.justDragged = false;

        // 重置两个控制条位置
        this.moveLeftControllerBar();
        this.moveRightControllerBar();

        // 重置播放范围
        if (this.audioLoaded) {
          const durationInSeconds = this.wavesurfer.getDuration();
          this.playbackRange = {
            startTime: "00:00",
            endTime: this.formatTime(durationInSeconds),
          };
        } else {
          this.playbackRange = {
            startTime: "00:00",
            endTime: "00:00",
          };
        }
      }
    },

    onWaveformHover(event) {
      // 如果任何控制条正在拖动，不使用波形图的悬停
      if (this.controlBars.left.isDragging || this.controlBars.right.isDragging)
        return;

      if (!this.wavesurfer || !this.audioLoaded || !this.$refs.hoverIndicator) {
        return;
      }

      const waveformRect = this.$refs.waveform.getBoundingClientRect();
      const mouseX = event.clientX - waveformRect.left;
      const waveformWidth = waveformRect.width;

      // 计算鼠标位置百分比（限制在0-100%）
      let positionPercent = (mouseX / waveformWidth) * 100;
      positionPercent = Math.max(0, Math.min(100, positionPercent));

      // 显示悬停指示线
      this.$refs.hoverIndicator.style.left = `${positionPercent}%`;
      this.$refs.hoverIndicator.style.display = "block";

      // ✅ 计算悬停位置对应的时间
      const duration = this.wavesurfer.getDuration();
      const hoverTimeInSeconds = (positionPercent / 100) * duration;
      this.hoverTime = this.formatTime(hoverTimeInSeconds);
    },

    onWaveformLeave() {
      // 如果任何控制条正在拖动，不隐藏指示器
      if (this.controlBars.left.isDragging || this.controlBars.right.isDragging)
        return;

      if (this.$refs.hoverIndicator) {
        this.$refs.hoverIndicator.style.display = "none";
      }
      this.hoverTime = "00:00";
    },

    // 以下是原有方法，保持不变
    addMarkerAtCurrentTime() {
      if (!this.wavesurfer || !this.audioLoaded) {
        this.$message && this.$message.warning("请先加载音频");
        return;
      }

      const currentTime = this.wavesurfer.getCurrentTime();
      this.$prompt("选择标记时间点", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
      })
        .then(({ value }) => {
          this.$message({
            type: "success",
            message: `标记名称：${
              value ? value : "未命名标点"
            }，时间为:${currentTime}`,
          });
          this.addCustomMarker(currentTime, this.getRandomColor());
        })
        .catch(() => {
          this.$message({
            type: "info",
            message: "取消输入",
          });
        });
    },

    addCustomMarker(time, label, color = "#ff5722") {
      if (!this.wavesurfer || !this.audioLoaded || !this.$refs.waveform) {
        console.warn("音频尚未加载完成");
        return null;
      }

      const container = this.$refs.waveform;

      // 计算标记点位置（百分比）
      const positionPercent = (time / this.duration) * 100;

      // 创建标记点容器
      const markerContainer = document.createElement("div");
      markerContainer.className = "custom-marker";
      markerContainer.style.position = "absolute";
      markerContainer.style.left = `${positionPercent}%`;
      markerContainer.style.top = "0";
      markerContainer.style.transform = "translateX(-50%)";
      markerContainer.style.height = "100%";
      markerContainer.style.zIndex = "10";
      markerContainer.style.cursor = "pointer";

      // 创建标记线
      const markerLine = document.createElement("div");
      markerLine.style.width = "2px";
      markerLine.style.height = "100%";
      markerLine.style.backgroundColor = color;
      markerLine.style.margin = "0 auto";

      // 创建标签
      const markerLabel = document.createElement("div");
      markerLabel.className = "marker-label";
      markerLabel.textContent = label || `标记点 ${this.markers.length + 1}`;
      markerLabel.style.position = "absolute";
      markerLabel.style.bottom = "100%";
      markerLabel.style.left = "50%";
      markerLabel.style.transform = "translateX(-50%)";
      markerLabel.style.backgroundColor = color;
      markerLabel.style.color = "white";
      markerLabel.style.padding = "2px 8px";
      markerLabel.style.borderRadius = "4px";
      markerLabel.style.fontSize = "12px";
      markerLabel.style.whiteSpace = "nowrap";
      markerLabel.style.opacity = "0.9";
      markerLabel.style.display = "none";

      // 添加到容器
      markerContainer.appendChild(markerLine);
      markerContainer.appendChild(markerLabel);
      container.appendChild(markerContainer);

      // 存储标记点信息
      const markerInfo = {
        id: `marker_${this.markerCount++}`,
        time: time,
        label: label || `标记点 ${this.markers.length}`,
        color: color,
        element: markerContainer,
        labelElement: markerLabel,
      };

      this.markers.push(markerInfo);

      // 添加交互事件
      markerContainer.addEventListener("mouseenter", () => {
        markerLabel.style.display = "block";
      });

      markerContainer.addEventListener("mouseleave", () => {
        markerLabel.style.display = "none";
      });

      markerContainer.addEventListener("click", (e) => {
        e.stopPropagation();
        this.wavesurfer.seekTo(time / this.duration);
        this.$emit("marker-clicked", markerInfo);
      });

      // 右键点击删除
      markerContainer.addEventListener("contextmenu", (e) => {
        e.preventDefault();
        if (confirm(`确定要删除标记点 "${markerInfo.label}" 吗？`)) {
          this.removeMarker(markerInfo.id);
        }
      });

      console.log(`已添加标记点: ${label} (${time.toFixed(2)}s)`);
      return markerInfo;
    },

    removeMarker(markerId) {
      const index = this.markers.findIndex((m) => m.id === markerId);
      if (index !== -1) {
        const marker = this.markers[index];
        if (marker.element && marker.element.parentNode) {
          marker.element.parentNode.removeChild(marker.element);
        }
        this.markers.splice(index, 1);
        this.$emit("marker-removed", markerId);
      }
    },

    formatTime(seconds) {
      if (isNaN(seconds)) return "00:00";
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      const paddedSecs = secs < 10 ? "0" + secs : secs;
      return `${mins}:${paddedSecs}`;
    },

    getRandomColor() {
      const colors = [
        "#FF5722",
        "#4CAF50",
        "#2196F3",
        "#FF9800",
        "#9C27B0",
        "#00BCD4",
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    },

    downloadFile() {
      this.$emit("download", {
        timeRange: this.playbackRange,
      });
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.initWavesurfer();
    });
  },

  watch: {
    audioFile: function loadAudio(audioFile) {
      if (!audioFile || !audioFile.raw) return;

      const file = audioFile.raw;
      this.error = "";
      this.audioLoaded = false;
      this.hoverTime = "00:00";
      this.playbackRange = {
        startTime: "00:00",
        endTime: "00:00",
      };
      this.shouldPlayFromRangeStart = false;
      this.justDragged = false;

      // 重置控制条状态
      this.controlBars = {
        left: {
          isDragging: false,
          startClientX: 0,
          startLeft: 0,
          current: 0,
        },
        right: {
          isDragging: false,
          startClientX: 0,
          startLeft: 0,
          current: 0,
        },
      };

      // 清除之前的标记点
      this.markers.forEach((marker) => {
        if (marker.element && marker.element.parentNode) {
          marker.element.parentNode.removeChild(marker.element);
        }
      });
      this.markers = [];
      this.markerCount = 0;

      if (!file.type.startsWith("audio/")) {
        console.error("请选择一个有效的音频文件");
        return;
      }

      this.loading = true;

      // 使用更高效的Object URL
      const objectUrl = URL.createObjectURL(file);

      // 显示加载状态
      if (this.$loading) {
        this.$loading({
          text: "正在加载音频...",
        });
      }

      this.wavesurfer.load(objectUrl);

      // 清理Object URL
      this.wavesurfer.on("ready", () => {
        URL.revokeObjectURL(objectUrl);
      });
    },
  },

  beforeDestroy() {
    // 清理拖动事件监听
    document.removeEventListener("mousemove", this.handleDragMove);

    // 清理所有标记点
    this.markers.forEach((marker) => {
      if (marker.element && marker.element.parentNode) {
        marker.element.parentNode.removeChild(marker.element);
      }
    });

    // 销毁Wavesurfer实例
    if (this.wavesurfer) {
      this.wavesurfer.destroy();
    }
  },
};
</script>

<style scoped lang="scss">
@import "src/common/style/index";

.audio-container {
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  width: 100%;
  position: relative;

  .audio-container-show {
    background: linear-gradient(180deg, #003d75, #232b56);
    padding: 20px;
    border-radius: $main_border_radius;

    .audio-container-reset {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      color: #ffffff;
      align-items: center;
      gap: 5px;
      cursor: pointer;
      font-size: 14px;
    }

    .audio-container-show-name {
      text-align: center;
      margin-bottom: 20px;
      color: #ffffff;
      font-size: 12px;
    }

    #waveform {
      width: 100%;
      position: relative;
      min-height: 128px;

      .control-bar-left,
      .control-bar-right {
        position: absolute;
        height: 100%;
        width: 10px;
        top: 0;
        border-radius: 6px;
        z-index: 999;
        cursor: move;
        transition: left 0.1s linear;
        user-select: none;

        &:active {
          cursor: move;
          opacity: 0.8;
        }
      }

      .control-bar-left {
        background-color: $main_theme_color;
        left: -10px;
        border-top-left-radius: 6px;
        border-bottom-left-radius: 6px;
      }

      .control-bar-right {
        background-color: #ff5722;
        left: calc(100% - 10px);
        border-top-right-radius: 6px;
        border-bottom-right-radius: 6px;
      }

      .hover-indicator {
        position: absolute;
        height: 100%;
        width: 2px;
        background-color: rgba(255, 255, 255, 0.4);
        top: 0;
        left: 0;
        pointer-events: none;
        z-index: 5;
        display: none;
        transition: left 0.05s linear;

        & > span {
          position: absolute;
          top: -25px;
          left: 50%;
          transform: translateX(-50%);
          color: rgba(255, 255, 255, 0.9);
          font-size: 12px;
          background: rgba(0, 0, 0, 0.7);
          padding: 2px 6px;
          border-radius: 3px;
          white-space: nowrap;
          min-width: 60px;
          text-align: center;
        }
      }
    }

    .wave-func {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-top: 20px;
      justify-content: flex-start;

      .player-icon {
        font-size: 28px !important;
        color: #ffffff;
        cursor: pointer;
        transition: all 0.3s ease;

        &:before {
          background-color: rgba(0, 0, 0, 0.1);
          padding: 5px 20px;
          border-radius: $main_border_radius;
          transition: background-color 0.3s ease;
        }

        &:hover {
          &:before {
            background-color: rgba(0, 0, 0, 0.37);
          }
        }
      }

      .audio-time {
        color: #ffffff;
        background-color: rgba(0, 0, 0, 0.1);
        padding: 5px 20px;
        border-radius: $main_border_radius;
      }

      .split-time {
        color: #ffffff;
        display: flex;
        flex-direction: row;
        gap: 20px;
        font-size: 14px;
      }

      .download {
        color: #ffffff;
        flex: 1;
        justify-content: flex-end;
        display: flex;

        &:first-child {
        }
      }
    }
  }

  .wave-func {
    display: flex;
    flex-direction: row;
    gap: 20px;
    align-items: center;
    justify-content: center;
    margin-top: 40px;
  }
}
</style>
